import React, { useState } from 'react';
import styled from 'styled-components';

export interface GameModeConfig {
  id: string;
  name: string;
  icon: string;
  description: string;
  timeLimit?: number; // sekunder
  specialRules?: string[];
  scoring?: 'normal' | 'reverse' | 'multiplied';
  extraChances?: number;
  targetScore?: number;
  maxRounds?: number;
  // Nytt: stöd för tärningsantal och max antal kast
  diceCount?: number;
  maxRolls?: number;
  // Nytt: serieläge (t.ex. Best of 3)
  seriesLength?: number;
  /** Dölj från avancerat-läget men behåll konfigurationen för huvudmenyn */
  hiddenInAdvanced?: boolean;
}

interface GameModeVariantsProps {
  className?: string;
  onModeSelect?: (mode: GameModeConfig) => void;
  currentMode?: string;
}

const VariantsContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 20px;
  color: white;
  max-width: 900px;
`;

const Title = styled.h3`
  margin: 0 0 20px 0;
  font-size: 18px;
  text-align: center;
  color: white;
`;

const ModeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  max-height: none;
  overflow-y: visible;
  padding-right: 8px;
  
  @media (min-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  
  @media (min-width: 900px) {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
`;

const ModeCard = styled.button<{ $isActive: boolean; $difficulty: 'easy' | 'medium' | 'hard'; $palette: number }>`
  background: ${props => {
    if (props.$isActive) return 'linear-gradient(135deg, #FFD700, #FFA500)';
    const palettes = [
      { easy: ['#4CAF50', '#8BC34A'], medium: ['#FF9800', '#FFC107'], hard: ['#F44336', '#E91E63'] },
      { easy: ['#2196F3', '#03A9F4'], medium: ['#7B1FA2', '#9C27B0'], hard: ['#D32F2F', '#C2185B'] },
      { easy: ['#009688', '#26A69A'], medium: ['#FF7043', '#FFA726'], hard: ['#8E24AA', '#6A1B9A'] },
      { easy: ['#3F51B5', '#5C6BC0'], medium: ['#AFB42B', '#CDDC39'], hard: ['#E64A19', '#F4511E'] }
    ];
    const set = palettes[props.$palette % palettes.length];
    const [c1, c2] = set[props.$difficulty];
    return `linear-gradient(135deg, ${c1}, ${c2})`;
  }};
  border: none;
  border-radius: 12px;
  padding: 16px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, ${props => (props.$isActive ? 0.2 : 0)});
    transition: background 0.3s ease;
  }
`;

const ModeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
`;

const ModeIcon = styled.div`
  font-size: 24px;
  min-width: 32px;
`;

const ModeName = styled.div`
  font-size: 16px;
  font-weight: bold;
  flex: 1;
`;

const DifficultyBadge = styled.div<{ $difficulty: 'easy' | 'medium' | 'hard' }>`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => {
    switch (props.$difficulty) {
      case 'easy':
        return 'rgba(76, 175, 80, 0.8)';
      case 'medium':
        return 'rgba(255, 152, 0, 0.8)';
      case 'hard':
        return 'rgba(244, 67, 54, 0.8)';
      default:
        return 'rgba(33, 150, 243, 0.8)';
    }
  }};
  text-transform: uppercase;
  font-weight: bold;
`;

const ModeDescription = styled.div`
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
  line-height: 1.3;
`;

const SpecialRules = styled.div`
  font-size: 12px;
  opacity: 0.8;
  position: relative;
  z-index: 1;
`;

const RuleItem = styled.div`
  margin-bottom: 2px;
  
  &:before {
    content: '• ';
    color: rgba(255, 255, 255, 0.7);
  }
`;

const TimeIndicator = styled.div<{ $urgent: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.$urgent ? 'rgba(244, 67, 54, 0.8)' : 'rgba(33, 150, 243, 0.8)'};
  margin-left: 8px;
`;

const GameModeVariants: React.FC<GameModeVariantsProps> = ({ 
  className, 
  onModeSelect, 
  currentMode = 'classic' 
}) => {
  const [selectedMode, setSelectedMode] = useState<string>(currentMode);

  const getDifficulty = (mode: GameModeConfig): 'easy' | 'medium' | 'hard' => {
    if (mode.timeLimit && mode.timeLimit <= 15) return 'hard';
    if (mode.timeLimit && mode.timeLimit <= 30) return 'medium';
    if (mode.scoring === 'reverse' || mode.targetScore) return 'hard';
    if (mode.extraChances || mode.maxRounds) return 'medium';
    return 'easy';
  };

  const getDifficultyLabel = (difficulty: 'easy' | 'medium' | 'hard'): string => {
    switch (difficulty) {
      case 'easy': return 'Lätt';
      case 'medium': return 'Medium';
      case 'hard': return 'Svår';
    }
  };

  const handleModeSelect = (mode: GameModeConfig) => {
    setSelectedMode(mode.id);
    onModeSelect?.(mode);
  };

  return (
    <VariantsContainer className={className}>
      <Title>🎮 Spellägen</Title>
      
      <ModeGrid>
        {gameModes.filter(mode => !mode.hiddenInAdvanced).map((mode, idx) => {
          const difficulty = getDifficulty(mode);
          return (
            <ModeCard
              key={mode.id}
              $isActive={selectedMode === mode.id}
              $difficulty={difficulty}
              $palette={idx}
              onClick={() => handleModeSelect(mode)}
            >
              <ModeHeader>
                <ModeIcon>{mode.icon}</ModeIcon>
                <ModeName>{mode.name}</ModeName>
                <DifficultyBadge $difficulty={difficulty}>
                  {getDifficultyLabel(difficulty)}
                </DifficultyBadge>
                {mode.timeLimit && (
                  <TimeIndicator $urgent={mode.timeLimit <= 20}>
                    ⏱️ {mode.timeLimit}s
                  </TimeIndicator>
                )}
              </ModeHeader>
              
              <ModeDescription>
                {mode.description}
              </ModeDescription>
              
              {mode.specialRules && (
                <SpecialRules>
                  {mode.specialRules.map((rule, index) => (
                    <RuleItem key={index}>{rule}</RuleItem>
                  ))}
                </SpecialRules>
              )}
            </ModeCard>
          );
        })}
      </ModeGrid>
    </VariantsContainer>
  );
};

export const gameModes: GameModeConfig[] = [
  {
    id: 'classic',
    name: 'Klassiskt Yatzy',
    icon: '🎲',
    description: 'Det traditionella Yatzy-spelet med standardregler',
    scoring: 'normal',
    hiddenInAdvanced: true
  },
  {
    // Dagens utmaning: seedade tärningar per UTC-dag (se todaySeed i utils/rng)
    // och egen topplista per dag (daily:YYYY-MM-DD). Startas från startskärmen
    // eller via ?shortcut=daily – visas inte i Avancerade lägen.
    id: 'daily',
    name: 'Dagens utmaning',
    icon: '📅',
    description: 'Samma tärningsserie för alla idag – tävla på dagens topplista',
    scoring: 'normal',
    specialRules: ['Tärningarna är seedade per dag (UTC)', 'Egen topplista för varje dag'],
    hiddenInAdvanced: true
  },
  {
    id: 'best-of-3',
    name: 'Best of 3',
    icon: '🥇',
    description: 'Spela tre omgångar – bästa resultatet räknas',
    scoring: 'normal',
    specialRules: ['Tre spel i rad', 'Bästa resultat publiceras'],
    seriesLength: 3,
    diceCount: 5,
    maxRolls: 3
  },
  {
    id: 'forced-classic',
    name: 'Tvingande (Classic)',
    icon: '📏',
    description: 'Spela klassiskt men du måste fylla kategorierna i ordning',
    scoring: 'normal',
    specialRules: ['Måste fyllas i fördefinierad ordning', 'Strategisk planering krävs'],
    hiddenInAdvanced: true
  },
  {
    id: 'maxi',
    name: 'Maxi Yatzy',
    icon: '🎲',
    description: 'Spela med 6 tärningar. Yatzy kräver sex lika.',
    scoring: 'normal',
    specialRules: ['6 tärningar', 'Yatzy vid exakt 6 lika'],
    diceCount: 6,
    maxRolls: 3,
    hiddenInAdvanced: true
  },
  {
    id: 'forced-maxi',
    name: 'Tvingande (Maxi)',
    icon: '🧭',
    description: 'Maxi-varianten där kategorierna måste fyllas i exakt ordning',
    scoring: 'normal',
    specialRules: ['Obligatorisk ordningsföljd', '6 tärningar'],
    diceCount: 6,
    maxRolls: 3,
    hiddenInAdvanced: true
  },
  {
    id: 'speed',
    name: 'Speed Yatzy',
    icon: '⚡',
    description: 'Snabbt tempo! Du har bara 30 sekunder per omgång',
    timeLimit: 30,
    scoring: 'normal',
    specialRules: ['30 sekunder per runda', 'Tiden ute = strykning (0 poäng)']
  },
  {
    id: 'reverse',
    name: 'Reverse Yatzy',
    icon: '🔄',
    description: 'Lägst poäng vinner och kategorierna spelas i omvänd ordning',
    scoring: 'reverse',
    specialRules: ['Lägsta poäng vinner', 'Yatzy först, avsluta med ettor']
  },
  {
    id: 'blitz',
    name: 'Blitz Mode',
    icon: '💨',
    description: 'Blixtsnabbt! 15 sekunder per drag',
    timeLimit: 15,
    // CHANGE (2026-02-23): Blitz ska inte ge dubbla poäng (endast snabbare tempo).
    scoring: 'normal',
    specialRules: ['15 sekunder per runda', 'Tiden ute = strykning (0 poäng)']
  }
];

export default GameModeVariants;
